/**
 * useContracts — TanStack Query data layer for the Contracts module.
 *
 * A contract closes the flow: approved quote → SIGNED CONTRACT → service order →
 * receipt. It is a FLAT entity (header + text body + two signatures): the
 * provider signs in-app (authenticated) and the client/taker signs remotely via
 * a public link. Status becomes 'signed' only when BOTH signatures exist.
 *
 * Offline-first applies ONLY to the DRAFT (create/update/delete) — delegated to
 * useOfflineSync, which enqueues the op and updates the local cache. An
 * `offline_` id must never reach Postgres — see [[orcamigo-offline-sync-uuid-bug]].
 *
 * Sending for signature and provider-signing are ONLINE-only (they need to mint
 * a public token, capture a server timestamp, etc.) and are never enqueued.
 */
import { useCallback, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { useCompany } from '@/contexts/useCompany';
import { supabase } from '@/integrations/supabase/client';
import { checkNetworkStatus } from '@/hooks/useNetworkStatus';
import { useOfflineSync } from '@/hooks/useOfflineSync';
import { cacheContracts, getCachedContracts } from '@/lib/offlineDataCache';
import { getPublicContractUrl } from '@/lib/urls';
import { CONTRACT_CLAUSE_COLUMNS } from '@/lib/contractClauses';
import { toast } from 'sonner';

const CONTRACTS_KEY = 'contracts';

/** Days a signing link stays valid after being sent. */
const LINK_VALIDITY_DAYS = 30;

export type ContractStatus = 'draft' | 'sent' | 'partially_signed' | 'signed' | 'cancelled';

export const CONTRACT_STATUSES: { value: ContractStatus; label: string }[] = [
  { value: 'draft', label: 'Rascunho' },
  { value: 'sent', label: 'Enviado' },
  { value: 'partially_signed', label: 'Parcial' },
  { value: 'signed', label: 'Assinado' },
  { value: 'cancelled', label: 'Cancelado' },
];

export function contractStatusLabel(status: ContractStatus): string {
  return CONTRACT_STATUSES.find((s) => s.value === status)?.label ?? status;
}

/** The contract body is frozen once the contract leaves 'draft' (tamper-evidence). */
export function isContentLocked(status?: string | null): boolean {
  return (status ?? 'draft') !== 'draft';
}

export interface ContractInput {
  quote_id?: string | null;
  client_id?: string | null;
  client_name?: string | null;
  client_phone?: string | null;
  client_address?: string | null;
  client_document?: string | null;
  title?: string | null;
  /**
   * Objeto do contrato — o "Serviço (resumo)" do formulário. É a variável que
   * alimenta a cláusula de objeto dos modelos; guardá-la é o que permite
   * REGERAR o corpo sem perder o objeto (antes ela era transitória e a
   * regeração caía no título).
   */
  service_description?: string | null;
  content?: string | null;
  status?: ContractStatus;
  total?: number;
  show_sequence_id?: boolean;

  // ─── Cláusulas estruturadas ────────────────────────────────────────────────
  // As variáveis que faziam o contrato ter prazo, multa e foro de verdade em
  // vez de frase genérica. Todas opcionais e nullable: ausência é "o contrato
  // não fala disso", nunca zero (#61). A LISTA canônica é
  // `CONTRACT_CLAUSE_COLUMNS` — estes campos existem para o `tsc` conferir os
  // call-sites, mas quem itera é a lista.
  execution_place?: string | null;
  start_date?: string | null;
  end_date?: string | null;
  payment_terms?: string | null;
  late_fee_percent?: number | null;
  interest_percent?: number | null;
  confidentiality_years?: number | null;
  notice_days?: number | null;
  termination_fee_percent?: number | null;
  forum_city?: string | null;
  signing_city?: string | null;
}

/** True when both provider and client have signed. */
export function isFullySigned(contract: any): boolean {
  return Boolean(contract?.provider_signed_at && contract?.client_signed_at);
}

/**
 * Next status after the PROVIDER signs in-app. 'signed' only when the client has
 * already signed (both parties present); 'partially_signed' if the contract was
 * already sent and now awaits the client; a pre-signed draft stays 'draft' until
 * it is actually sent. Pure so it can be unit-tested.
 */
export function nextStatusAfterProviderSign(
  currentStatus: ContractStatus,
  clientSigned: boolean,
): ContractStatus {
  if (clientSigned) return 'signed';
  if (currentStatus === 'sent') return 'partially_signed';
  return currentStatus;
}

/**
 * Next status after the CLIENT signs remotely (mirrors the contract-sign edge
 * function, which is the authoritative writer): 'signed' if the provider already
 * signed, else 'partially_signed'. Pure so it can be unit-tested.
 */
export function nextStatusAfterClientSign(providerSigned: boolean): 'signed' | 'partially_signed' {
  return providerSigned ? 'signed' : 'partially_signed';
}

/**
 * Mint a non-enumerable public token for the signing link. Uses the Web Crypto
 * RNG (24 bytes → base64url) so the link cannot be guessed; falls back to
 * randomUUID when getRandomValues is unavailable.
 */
function generatePublicToken(): string {
  try {
    const bytes = new Uint8Array(24);
    crypto.getRandomValues(bytes);
    let binary = '';
    for (const b of bytes) binary += String.fromCharCode(b);
    return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  } catch {
    // Fallback: two UUIDs concatenated (still 244 random bits).
    const uuid = () =>
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    return `${uuid()}${uuid()}`.replace(/-/g, '');
  }
}

// ===== Read =====

/**
 * Base query: every contract for the active company. Cached locally so the list
 * keeps working offline. Filtering is client-side to preserve the snapshot.
 */
export function useContracts() {
  const { user } = useAuth();
  const { activeCompany } = useCompany();
  const companyId = activeCompany?.id ?? null;
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: [CONTRACTS_KEY, companyId],
    enabled: Boolean(user?.id && companyId),
    networkMode: 'offlineFirst',
    staleTime: 60 * 1000,
    // Remote client signatures don't dispatch a local `contracts-updated` event,
    // so a provider reopening a contract would otherwise see a stale signature
    // state until staleTime elapses. Refetch on every mount (offline-first still
    // serves the cache instantly and when offline). Mirrors useFeature.
    refetchOnMount: 'always',
    // A query nasce DESABILITADA (sessão/empresa resolvem depois da montagem), então
    // `refetchOnMount` já passou quando ela habilita: sem o carimbo, o cache semeado
    // conta como FRESCO e a primeira busca nunca sai (#158).
    initialDataUpdatedAt: 0,
    initialData: () => (user?.id ? getCachedContracts(user.id) ?? undefined : undefined),
    queryFn: async (): Promise<any[]> => {
      if (!user?.id || !companyId) return [];
      const { data, error } = await supabase
        .from('contracts')
        .select('*')
        .eq('company_id', companyId)
        .order('created_at', { ascending: false });
      if (error) throw error;
      const rows = (data ?? []) as any[];
      cacheContracts(user.id, rows);
      return rows;
    },
  });

  const invalidate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: [CONTRACTS_KEY, companyId] });
  }, [queryClient, companyId]);

  const syncFromCache = useCallback(() => {
    if (!user?.id) return;
    queryClient.setQueryData([CONTRACTS_KEY, companyId], getCachedContracts(user.id) ?? []);
  }, [queryClient, user?.id, companyId]);

  return { ...query, contracts: query.data ?? [], invalidate, syncFromCache };
}

/**
 * `useContractsFiltered` foi REMOVIDO quando `/contratos` ganhou o desenho
 * oficial: a tela precisa do recorte SEM o status (é dele que saem as contagens
 * de cada botão da faixa — #193/#196), então um hook que devolve a lista já
 * filtrada não tinha mais consumidor. Mantê-lo "por conveniência" o deixaria
 * como uma SEGUNDA implementação da mesma regra, sem teste e sem ninguém
 * exercitando — o código morto com guard verde do #187. O recorte mora hoje em
 * `src/lib/contractsListFilters.ts`.
 */

/** A single contract by id, from the cached list (works offline). */
export function useContract(id: string | undefined) {
  const raw = useContracts();
  const contract = useMemo(
    () => (id ? raw.contracts.find((c: any) => c.id === id) ?? null : null),
    [raw.contracts, id],
  );
  return { ...raw, contract };
}

// ===== Mutations =====

/** Normalize an input into the scalar column set stored on `contracts`. */
function toContractFields(input: Partial<ContractInput>): Record<string, any> {
  const fields: Record<string, any> = {};
  const set = (key: string, value: any) => {
    if (value !== undefined) fields[key] = value;
  };
  set('quote_id', input.quote_id ?? null);
  set('client_id', input.client_id ?? null);
  set('client_name', input.client_name ?? null);
  set('client_phone', input.client_phone ?? null);
  set('client_address', input.client_address ?? null);
  set('client_document', input.client_document ?? null);
  set('title', input.title ?? null);
  set('service_description', input.service_description ?? null);
  set('content', input.content ?? null);
  set('status', input.status ?? 'draft');
  set('total', input.total !== undefined ? Number(input.total) : undefined);
  set('show_sequence_id', input.show_sequence_id);
  // As cláusulas ITERAM a lista canônica em vez de onze `set()` à mão: é a
  // única forma de este normalizador não envelhecer na próxima coluna (#89).
  for (const { column } of CONTRACT_CLAUSE_COLUMNS) {
    set(column, (input as Record<string, unknown>)[column]);
  }
  return fields;
}

export function useCreateContract() {
  const { user } = useAuth();
  const { activeCompany } = useCompany();
  const companyId = activeCompany?.id ?? null;
  const queryClient = useQueryClient();
  const { saveContractOffline } = useOfflineSync();

  return useMutation({
    // offlineFirst: sem isto o TanStack PAUSA a mutation quando o app perde a
    // rede com a tela aberta, e o ramo offline nunca roda (CLAUDE.md #41).
    networkMode: 'offlineFirst' as const,
    mutationFn: async (input: ContractInput) => {
      if (!user?.id) throw new Error('Sem sessão');
      if (!companyId) throw new Error('Nenhuma empresa ativa');

      const fields = toContractFields(input);
      const online = await checkNetworkStatus();

      if (!online) {
        const id = saveContractOffline({ contract: fields, userId: user.id, companyId });
        queryClient.setQueryData([CONTRACTS_KEY, companyId], getCachedContracts(user.id) ?? []);
        return { offline: true as const, id };
      }

      // Next per-company sequence.
      const { data: maxSeqRow } = await supabase
        .from('contracts')
        .select('sequence_id')
        .eq('company_id', companyId)
        .not('sequence_id', 'is', null)
        .order('sequence_id', { ascending: false })
        .limit(1)
        .maybeSingle();
      const nextSequenceId = ((maxSeqRow as any)?.sequence_id ?? 0) + 1;

      const { data: created, error } = await supabase
        .from('contracts')
        .insert({ ...fields, user_id: user.id, company_id: companyId, sequence_id: nextSequenceId })
        .select('*')
        .single();
      if (error) throw error;

      queryClient.invalidateQueries({ queryKey: [CONTRACTS_KEY, companyId] });
      window.dispatchEvent(new CustomEvent('contracts-updated'));
      return { offline: false as const, id: (created as any).id as string };
    },
    onError: () => toast.error('Não foi possível salvar o contrato.'),
  });
}

export function useUpdateContract() {
  const { user } = useAuth();
  const { activeCompany } = useCompany();
  const companyId = activeCompany?.id ?? null;
  const queryClient = useQueryClient();
  const { updateContractOffline } = useOfflineSync();

  return useMutation({
    // offlineFirst: sem isto o TanStack PAUSA a mutation quando o app perde a
    // rede com a tela aberta, e o ramo offline nunca roda (CLAUDE.md #41).
    networkMode: 'offlineFirst' as const,
    mutationFn: async (input: { id: string; updates: Partial<ContractInput>; currentStatus?: string | null }) => {
      if (!user?.id) throw new Error('Sem sessão');
      const online = await checkNetworkStatus();

      // Freeze the body once the contract has left 'draft' (tamper-evidence).
      const updates = { ...input.updates };
      const status =
        input.currentStatus ??
        (getCachedContracts(user.id) || []).find((c: any) => c.id === input.id)?.status ??
        'draft';
      if (isContentLocked(status) && updates.content !== undefined) {
        delete updates.content;
      }

      const fields = toContractFields(updates);
      // Never let an update blank a field that wasn't provided.
      Object.keys(fields).forEach((k) => {
        if ((updates as any)[k] === undefined) delete fields[k];
      });

      if (!online) {
        updateContractOffline({ contractId: input.id, updates: fields, userId: user.id });
        queryClient.setQueryData([CONTRACTS_KEY, companyId], getCachedContracts(user.id) ?? []);
        return;
      }

      let query = supabase.from('contracts').update(fields).eq('id', input.id);
      if (companyId) query = query.eq('company_id', companyId);
      const { error } = await query;
      if (error) throw error;

      queryClient.invalidateQueries({ queryKey: [CONTRACTS_KEY, companyId] });
      window.dispatchEvent(new CustomEvent('contracts-updated'));
    },
    onError: () => toast.error('Não foi possível atualizar o contrato.'),
  });
}

export function useDeleteContract() {
  const { user } = useAuth();
  const { activeCompany } = useCompany();
  const companyId = activeCompany?.id ?? null;
  const queryClient = useQueryClient();
  const { deleteContractOffline } = useOfflineSync();

  return useMutation({
    // offlineFirst: sem isto o TanStack PAUSA a mutation quando o app perde a
    // rede com a tela aberta, e o ramo offline nunca roda (CLAUDE.md #41).
    networkMode: 'offlineFirst' as const,
    mutationFn: async (id: string) => {
      if (!user?.id) throw new Error('Sem sessão');
      const online = await checkNetworkStatus();

      if (!online) {
        deleteContractOffline({ contractId: id, userId: user.id });
        queryClient.setQueryData([CONTRACTS_KEY, companyId], getCachedContracts(user.id) ?? []);
        return;
      }

      let query = supabase.from('contracts').delete().eq('id', id);
      if (companyId) query = query.eq('company_id', companyId);
      const { error } = await query;
      if (error) throw error;
      queryClient.invalidateQueries({ queryKey: [CONTRACTS_KEY, companyId] });
      window.dispatchEvent(new CustomEvent('contracts-updated'));
    },
    onError: () => toast.error('Não foi possível excluir o contrato.'),
  });
}

/**
 * Provider signs the contract in-app (authenticated). ONLINE only. Records the
 * provider signature and marks the contract 'signed' if the client has already
 * signed, or 'partially_signed' if the contract was already sent and awaits the
 * client. A pre-signed draft stays 'draft' until it is actually sent.
 */
export function useProviderSignContract() {
  const { user } = useAuth();
  const { activeCompany } = useCompany();
  const companyId = activeCompany?.id ?? null;
  const queryClient = useQueryClient();

  return useMutation({
    // offlineFirst: sem isto o TanStack PAUSA a mutation quando o app perde a
    // rede com a tela aberta, e o ramo offline nunca roda (CLAUDE.md #41).
    networkMode: 'offlineFirst' as const,
    mutationFn: async (input: { contract: any; signatureDataUrl: string; signerName: string }) => {
      if (!user?.id) throw new Error('Sem sessão');
      const online = await checkNetworkStatus();
      if (!online) throw new Error('offline');
      if (!input.signatureDataUrl) throw new Error('Assinatura obrigatória');
      const name = input.signerName?.trim();
      if (!name) throw new Error('Informe o nome de quem assina');

      const current = input.contract;
      const clientSigned = Boolean(current?.client_signed_at);
      const currentStatus = (current?.status ?? 'draft') as ContractStatus;
      const nextStatus = nextStatusAfterProviderSign(currentStatus, clientSigned);

      const updates = {
        provider_signature: input.signatureDataUrl,
        provider_signer_name: name,
        provider_signed_at: new Date().toISOString(),
        status: nextStatus,
      };

      let query = supabase.from('contracts').update(updates).eq('id', current.id);
      if (companyId) query = query.eq('company_id', companyId);
      const { error } = await query;
      if (error) throw error;

      queryClient.invalidateQueries({ queryKey: [CONTRACTS_KEY, companyId] });
      window.dispatchEvent(new CustomEvent('contracts-updated'));
      return { status: nextStatus };
    },
    onError: (e: any) =>
      toast.error(
        e?.message === 'offline'
          ? 'Assinar o contrato exige conexão com a internet.'
          : 'Não foi possível registrar a assinatura.',
      ),
  });
}

/**
 * Send the contract for remote signature. ONLINE only. Mints the public token,
 * stamps sent_at/expires_at and moves the status to 'sent'. Returns the shareable
 * public link (`/c/:token`). Editing the body is blocked from 'sent' onward.
 */
export function useSendContract() {
  const { user } = useAuth();
  const { activeCompany } = useCompany();
  const companyId = activeCompany?.id ?? null;
  const queryClient = useQueryClient();

  return useMutation({
    // offlineFirst: sem isto o TanStack PAUSA a mutation quando o app perde a
    // rede com a tela aberta, e o ramo offline nunca roda (CLAUDE.md #41).
    networkMode: 'offlineFirst' as const,
    mutationFn: async (input: { contract: any }) => {
      if (!user?.id) throw new Error('Sem sessão');
      const online = await checkNetworkStatus();
      if (!online) throw new Error('offline');

      const current = input.contract;
      if (!current?.content?.trim()) throw new Error('empty');
      if (current.status === 'signed' || current.status === 'cancelled') throw new Error('bad_status');
      // The CPF challenge anchors on client_document — without it the remote
      // signature has no identity proof, so require it before sending.
      if (!current?.client_document?.trim()) throw new Error('no_document');

      // Reuse an existing token if the contract was already sent (re-share) so
      // the previous link stays valid; otherwise mint a fresh one.
      const token: string = current.public_token || generatePublicToken();
      const now = new Date();
      const expires = new Date(now.getTime() + LINK_VALIDITY_DAYS * 24 * 60 * 60 * 1000);

      const updates: Record<string, any> = {
        public_token: token,
        sent_at: current.sent_at ?? now.toISOString(),
        expires_at: expires.toISOString(),
        // If EITHER party already signed (e.g. a pre-signed draft, or a client
        // who signed via an earlier link), the contract is 'partially_signed' —
        // never downgrade to 'sent'. Only a contract with no signatures becomes
        // 'sent'. ('signed' is blocked from re-send by the guard above.)
        status: current.provider_signed_at || current.client_signed_at ? 'partially_signed' : 'sent',
      };

      let query = supabase.from('contracts').update(updates).eq('id', current.id);
      if (companyId) query = query.eq('company_id', companyId);
      const { error } = await query;
      if (error) throw error;

      queryClient.invalidateQueries({ queryKey: [CONTRACTS_KEY, companyId] });
      window.dispatchEvent(new CustomEvent('contracts-updated'));
      return { url: getPublicContractUrl(token), token };
    },
    onError: (e: any) => {
      const map: Record<string, string> = {
        offline: 'Enviar o contrato para assinatura exige conexão com a internet.',
        empty: 'Escreva o corpo do contrato antes de enviar.',
        bad_status: 'Este contrato não pode mais ser enviado.',
      };
      toast.error(map[e?.message] ?? 'Não foi possível enviar o contrato.');
    },
  });
}
